import * as Blockly from 'blockly/core';
import { blockIcon } from '@/(routers)/boards/editor/block/blockFactory';
import { defineMutableBlock } from '@/(routers)/boards/editor/block/blockRegistry';
import { FieldStyledBadgeLabel } from '@/(routers)/boards/editor/block/definitions/fields/fieldStyledBadgeLabel';
import { FieldStyledLabel } from '@/(routers)/boards/editor/block/definitions/fields/fieldStyledLabel';

const COLOR = '#AE78E7';
const ICON_ADD = { src: '/images/block/btn_add.svg', size: 20 };
const ICON_REMOVE = { src: '/images/block/btn_remove.svg', size: 20 };
const ICON_GRAPH = '/images/block/monitor_graph.svg';

function getIds(block: Blockly.Block): string[] {
    return (block as any)._ids as string[];
}

function appendHeader(block: Blockly.Block): void {
    block
        .appendDummyInput('HEADER')
        .appendField(blockIcon({ src: ICON_GRAPH, size: 70 }), 'ICON')
        .appendField(new FieldStyledLabel('グラフモニター', { fontSize: 15, fontWeight: 'bold' }))
        .appendField(' ')
        .appendField(
            new Blockly.FieldImage(ICON_ADD.src, ICON_ADD.size, ICON_ADD.size, '+', function () {
                addValue(block);
            }),
            'ADD',
        )
        .appendField(
            new Blockly.FieldImage(ICON_REMOVE.src, ICON_REMOVE.size, ICON_REMOVE.size, '-', function () {
                removeLastValue(block);
            }),
            'REMOVE',
        );
}

function refreshFooter(block: Blockly.Block): void {
    if (block.getInput('FOOTER')) block.removeInput('FOOTER');
    block.appendValueInput('FOOTER').setCheck('__none__').appendField('を出力する');
}

function attachShadow(block: Blockly.Block, inputName: string, type: 'string' | 'number'): void {
    const input = block.getInput(inputName);
    if (!input?.connection || input.connection.targetBlock()) return;

    const shadow = (block.workspace as Blockly.WorkspaceSvg).newBlock(type) as Blockly.BlockSvg;
    shadow.setShadow(true);
    shadow.outputConnection?.connect(input.connection);

    requestAnimationFrame(() => {
        shadow.initSvg();
        shadow.render();
    });
}

function appendValueRow(block: Blockly.Block, id: string, index: number): void {
    if (index > 1) {
        block.appendValueInput(`SPACER_${id}`).setCheck('__none__').appendField(new Blockly.FieldLabel(''));
    }
    block
        .appendValueInput(`LABEL_${id}`)
        .setCheck('String')
        .appendField(new FieldStyledBadgeLabel(`グラフ${index}`))
        .appendField(new FieldStyledLabel('ラベルは', { fontSize: 14 }));

    block
        .appendValueInput(`VAL_${id}`)
        .setCheck('Number')
        .appendField(new FieldStyledLabel('値は', { fontSize: 14 }));

    attachShadow(block, `LABEL_${id}`, 'string');
    attachShadow(block, `VAL_${id}`, 'number');
}

function addValue(block: Blockly.Block): void {
    Blockly.Events.disable();
    try {
        const ids = getIds(block);
        const newId = Math.random().toString(36).slice(2, 8);
        ids.push(newId);
        appendValueRow(block, newId, ids.length);
    } finally {
        Blockly.Events.enable();
        refreshFooter(block);
        (block.workspace as Blockly.WorkspaceSvg).render();
    }
}

function removeLastValue(block: Blockly.Block): void {
    const ids = getIds(block);
    if (ids.length <= 1) return;

    const lastId = ids[ids.length - 1];

    Blockly.Events.disable();
    try {
        for (const inputName of [`SPACER_${lastId}`, `LABEL_${lastId}`, `VAL_${lastId}`]) {
            const target = block.getInput(inputName)?.connection?.targetBlock();
            if (target && !target.isShadow()) target.dispose(false);
            if (block.getInput(inputName)) block.removeInput(inputName);
        }
        ids.pop();
    } finally {
        Blockly.Events.enable();
        refreshFooter(block);
        (block.workspace as Blockly.WorkspaceSvg).render();
    }
}

defineMutableBlock({
    type: 'monitor_graph',
    colour: COLOR,
    tooltip: 'グラフモニターに値を送信します',
    movable: true,
    deletable: true,
    previousStatement: true,
    nextStatement: true,

    init(this: Blockly.Block) {
        (this as any)._ids = [] as string[];
        this.setColour(COLOR);
        this.setTooltip('グラフモニターに値を送信します');
        this.setMovable(true);
        this.setDeletable(true);
        this.setPreviousStatement(true);
        this.setNextStatement(true);

        const firstId = Math.random().toString(36).slice(2, 8);
        (this as any)._ids.push(firstId);

        appendHeader(this);
        appendValueRow(this, firstId, 1);
        refreshFooter(this);
    },

    mutationToDom(this: Blockly.Block): Element {
        const container = Blockly.utils.xml.createElement('mutation');
        container.setAttribute('ids', getIds(this).join(','));
        return container;
    },

    domToMutation(this: Blockly.Block, xml: Element): void {
        for (const id of getIds(this)) {
            if (this.getInput(`LABEL_${id}`)) this.removeInput(`LABEL_${id}`);
            if (this.getInput(`VAL_${id}`)) this.removeInput(`VAL_${id}`);
        }
        if (this.getInput('HEADER')) this.removeInput('HEADER');
        if (this.getInput('FOOTER')) this.removeInput('FOOTER');

        const raw = xml.getAttribute('ids') ?? '';
        const ids = raw ? raw.split(',').filter(Boolean) : [];

        if (ids.length === 0) {
            ids.push(Math.random().toString(36).slice(2, 8));
        }

        (this as any)._ids = ids;

        appendHeader(this);
        ids.forEach((id, i) => appendValueRow(this, id, i + 1));
        refreshFooter(this);

        this.setOutputShape(3);
        if (this.isShadow()) {
            (this as Blockly.BlockSvg).render();
        }
    },
});
